// @givmo/compliance — the words and the pure rules of sending a filing's form for signature and
// following it (CCM-R1-SIGN-WEB-B, the issuer's side; slice 2 §6(a), (d), (e), frames 31, 32, 34,
// 36, 37, 41–44 and 46, approved as shown, D-CCM-22).
//
// ══ WHERE EVERY LINE COMES FROM ════════════════════════════════════════════════════════════════
//
// APPROVED: slice 2's frames and its words-only block (`NEW-STRINGS-FOR-APPROVAL.md`, item 30,
// D-CCM-22). SERVED, rendered as served and never re-worded here: the rule's signer roles (B4), the
// factual line, every refusal's `detail`, the record's lines (B6) and the files' names. APPROVED by
// Jonathan on this cut's renders (D-CCM-32): S6 (D-SIGN-12's line, less the loss date the backend
// doesn't serve) and S12 (a signed place's word on a request still open) — both shown only on a
// request with two or more places, which no Maryland form has. No line here carries a root the
// suite's terminology rail bans.
//
// ══ WHAT THE SEND SENDS ════════════════════════════════════════════════════════════════════════
//
// The version the person is looking at (recorded by the Download rule), one designation per place
// its form declares (the signer and the title as the organization states it), the first way the
// filing is sent that Givmo can sign (the rule's order; 1002-7's Q5), the browser's zone, the end of
// the chosen sign-by day in that zone (the last day the server's 90 days reach sends their limit; a
// later day is the server's to refuse) and the note. The server decides everything else and says
// why in its own words.

import type { SigningRequestRow } from './compliance-preparation-decode';
import type {
  SignerRequirement,
  SigningAct,
  SigningChannel,
  SigningPlace,
  SigningRequestDetail,
  SigningSlot,
} from './compliance-signing-decode';
import { localDayOf } from './compliance-decode';
import { beforeFirstSolicitLine } from './compliance-panel-model';
import {
  SOMEONE_IN_YOUR_ORGANIZATION,
  SOMEONE_IN_YOUR_ORGANIZATION_MID_SENTENCE,
  longDateIn,
  shortDateIn,
} from './compliance-signing-model';

type PersonName = ((userId: string) => string | undefined) | undefined;

// ── The workbench (frames 31, 32, 34, 37) ──────────────────────────────────────────────────────

export const SEND_FOR_SIGNATURE = 'Send for signature';
export const ANSWER_EVERY_ITEM_LINE = 'Answer every item to send it for signature.';
export const OWNER_ONLY_LINE =
  'Only a Compliance Owner or a Global Admin can send documents for signature.';

// ── The send sheet (frame 36) ──────────────────────────────────────────────────────────────────

export const PREVIEW = 'Preview';
export const ATTACHMENTS_GO_LINE =
  'The attachments go with the form when it’s sent. They aren’t signed here.';
export const WHO_SIGNS = 'Who signs';
export const SIGNER_LABEL = 'Signer';
export const TITLE_AS_STATED_LABEL = 'Title, as your organization states it';
export const SOMETHING_ELSE = 'Something else…';
export const TITLE_RECORDED_LINE =
  'Givmo records the title as you state it. It doesn’t check that the person holds the office.';
export const ONLY_PEOPLE_WITH_ACCESS = 'Only people with access to Compliance can sign.';
export const INVITE_TO_COMPLIANCE = 'Invite someone to Compliance';
export const SIGN_BY = 'Sign by';
export const EXTEND_LATER_LINE = 'You can extend it later, up to 90 days from today.';
export const MESSAGE_OPTIONAL = 'Message (optional)';
export const NOTE_PLACEHOLDER = 'A note for the signer';

// ── Request status (frames 41–44) ──────────────────────────────────────────────────────────────

export const OUT_FOR_SIGNATURE = 'Out for signature';
export const EXPIRED = 'Expired';
export const CANCELLED = 'Cancelled';
export const WAITING = 'Waiting';
export const NOT_SIGNED = 'Not signed';
export const CANT_SIGN = 'Can’t sign';
export const EXTEND_THE_DATE = 'Extend the date';
export const CHANGE_THE_SIGNER = 'Change the signer or title';
export const CANCEL_THE_REQUEST = 'Cancel the request';
export const EXTEND = 'Extend';
export const CHANGE_AN_ANSWER = 'Change an answer';
export const CHANGE_AN_ANSWER_TITLE = 'Change an answer?';
export const CANCEL_AND_CHANGE = 'Cancel the request and change it';
export const CANCEL_THIS_REQUEST = 'Cancel this request?';
/** The panel's built screen-reader name for a filing's "…" menu, reused for its request's. */
export const MORE_FOR_THIS_FILING = 'More for this filing';

// ── The signed form, its record and export (frame 46) ──────────────────────────────────────────

export const THE_SIGNED_FORM = 'The signed form';
export const SIGNING_RECORD = 'Signing record';
export const EXPORT_EVERYTHING = 'Export everything (.zip)';
export const DOWNLOAD_RECORD_PDF = 'Download the signing record (PDF)';
export const DOWNLOAD_RECORD_JSON = 'Download it as data (JSON)';
export const DOWNLOAD_AS_SIGNED_NOTE = 'Download gives you this exact PDF, as signed.';

// ── The sheet's lines ──────────────────────────────────────────────────────────────────────────

/** "4 pages", "1 page". */
function pagesWord(count: number): string {
  return count === 1 ? '1 page' : `${count} pages`;
}

/** "As prepared Nov 16, 2026 · version 3 · 4 pages": the version a send will name. */
export function versionLine(createdAt: string | undefined, number: number, pages: number): string {
  return `As prepared ${shortDateIn(createdAt)} · version ${number} · ${pagesWord(pages)}`;
}

/**
 * The form and when it is for, as the sheet names it: "Annual Update of Registration Form · fiscal
 * year ended June 30, 2026", or COR-92's "… · before you first solicit in Maryland".
 */
export function formLine(
  formNumber: string,
  when: { periodEnd?: string; firstSolicitIn?: string },
): string {
  if (when.periodEnd !== undefined) {
    return `${formNumber} · fiscal year ended ${longDateIn(when.periodEnd)}`;
  }
  if (when.firstSolicitIn !== undefined && when.firstSolicitIn.length > 0) {
    return `${formNumber} · ${beforeFirstSolicitLine(when.firstSolicitIn).replace(/^B/, 'b')}`;
  }
  return formNumber;
}

/**
 * A title inside the requirement sentence. The catalog's catch-all ("Other principal officer") reads
 * lowercased there, as frame 36 prints it (1002-7's P2); a title stays as served everywhere else.
 */
function roleInSentence(role: string): string {
  return /^Other\s/.test(role) ? `o${role.slice(1)}` : role;
}

/** "A, B or C". */
function orList(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} or ${parts[parts.length - 1]}`;
}

/**
 * "Maryland’s form asks for one signature, by the President, Chairman or other principal
 * officer.": the rule's recorded requirement (B4), its titles in the order served (the Annual
 * Update's is the statute's, "Chairman, President or other principal officer": a named difference
 * from frame 36). Nothing for a requirement other than one signature, or with no titles: there are
 * no approved words for it.
 */
export function requirementSentence(
  jurisdiction: string,
  requirement: SignerRequirement | undefined,
): string | undefined {
  if (requirement === undefined || requirement.signatureCount !== 1) return undefined;
  const roles = requirement.roles.map((role) => role.trim()).filter((role) => role.length > 0);
  const [first] = roles;
  if (first === undefined || jurisdiction.length === 0) return undefined;
  const article = /^the\s/i.test(first) ? '' : 'the ';
  return `${jurisdiction}’s form asks for one signature, by ${article}${orList(roles.map(roleInSentence))}.`;
}

/** "Signature on page 4". */
export function placeLine(page: number): string {
  return `Signature on page ${page}`;
}

/**
 * The way the filing will be sent, as the places read names it (`send_channel`,
 * CCM-R1-SIGN-SEND-CHANNEL: email where Givmo will email the filing), else, from an older backend,
 * {@link chooseChannel}'s first way Givmo can sign.
 */
export function sendChannelOf(places: {
  channels: readonly SigningChannel[];
  sendChannel?: string;
}): SigningChannel | undefined {
  return (
    places.channels.find((channel) => channel.channel === places.sendChannel) ??
    chooseChannel(places.channels)
  );
}

/**
 * The way the filing will be sent: the first, in the rule's order, that Givmo can sign (supported,
 * or signable with the factual line); an unreadable answer is never taken for one. Where none is,
 * the first, so the server's own refusal speaks.
 */
export function chooseChannel(channels: readonly SigningChannel[]): SigningChannel | undefined {
  return (
    channels.find(
      (channel) =>
        channel.support.state === 'supported' || channel.support.state === 'not_recorded',
    ) ?? channels[0]
  );
}

// ── Sign by ────────────────────────────────────────────────────────────────────────────────────

const DAY_MS = 24 * 60 * 60 * 1000;
/** The server's default and its maximum (`COMPLIANCE_SIGNING_REQUEST_*_TTL_SECONDS`). */
export const SIGN_BY_DEFAULT_DAYS = 14;
export const SIGN_BY_MAX_DAYS = 90;
/** Kept short of the server's maximum, so a browser clock a little ahead is never refused for it. */
const MAX_MARGIN_MS = 60 * 60 * 1000;

function isoDayOf(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** The sheet's default sign-by day: 14 days from today, as `YYYY-MM-DD` in the browser's zone. */
export function defaultSignByDay(now: Date): string {
  return isoDayOf(new Date(now.getTime() + SIGN_BY_DEFAULT_DAYS * DAY_MS));
}

/** The latest instant a sign-by date may be: the server's 90 days, less the margin. */
function latestSignByMs(now: Date): number {
  return now.getTime() + SIGN_BY_MAX_DAYS * DAY_MS - MAX_MARGIN_MS;
}

/**
 * The civil day an instant falls on in a zone: the browser's own where none is named, or one it
 * doesn't know.
 */
function dayIn(at: number, timeZone?: string): string {
  if (timeZone !== undefined) {
    try {
      const day = localDayOf(new Date(at).toISOString(), timeZone);
      if (day !== undefined) return day;
    } catch {
      // A zone this browser doesn't know: the browser's own, as `endOfDay` falls back.
    }
  }
  return isoDayOf(new Date(at));
}

/**
 * The latest day a sign-by date may fall on: the day the server's 90 days reach, in the zone the
 * date is written in (the browser's own where none is named).
 */
export function latestSignByDay(now: Date, timeZone?: string): string {
  return dayIn(latestSignByMs(now), timeZone);
}

/** A zone's offset from UTC at one instant, in ms (its wall clock there, less UTC). */
function zoneOffsetMs(at: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  }).formatToParts(new Date(at));
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);
  const wall = Date.UTC(
    value('year'),
    value('month') - 1,
    value('day'),
    value('hour'),
    value('minute'),
    value('second'),
  );
  return wall - Math.floor(at / 1000) * 1000;
}

/** 23:59 on a day in a zone (the browser's own where none is named), as an instant. */
function endOfDay(year: number, month: number, date: number, timeZone?: string): number {
  if (timeZone !== undefined) {
    try {
      const wall = Date.UTC(year, month - 1, date, 23, 59);
      // Twice, so a day a clock change falls on still ends at its own 23:59.
      const first = wall - zoneOffsetMs(wall, timeZone);
      return wall - zoneOffsetMs(first, timeZone);
    } catch {
      // A zone this browser doesn't know: the browser's own.
    }
  }
  return new Date(year, month - 1, date, 23, 59, 0, 0).getTime();
}

/**
 * The instant a chosen sign-by day sends: the end of that day in the request's zone (the browser's
 * own for a new request). The last day the server's 90 days reach can end after them, so it sends
 * their limit, still on that day; a later day sends its own end, and the server's refusal says why
 * in its own words. So a request never goes out for a different day than the one chosen (review-54's
 * F1). An unreadable day sends nothing.
 */
export function signByInstant(day: string, now: Date, timeZone?: string): string | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (match === null) return undefined;
  const end = endOfDay(Number(match[1]), Number(match[2]), Number(match[3]), timeZone);
  if (Number.isNaN(end)) return undefined;
  const capped = day <= latestSignByDay(now, timeZone);
  return new Date(capped ? Math.min(end, latestSignByMs(now)) : end).toISOString();
}

/** The day an open request's sign-by date falls on, in its own zone, as its card says it. */
export function currentSignByDay(detail: SigningRequestDetail): string {
  return localDayOf(detail.request.expiresAt, detail.version.timeZone) ?? '';
}

/** The browser's own zone, the one the request writes its dates in. */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/** One designation per place: who signs it and the title the organization states. */
export function designationsFor(
  documentId: string,
  places: readonly SigningPlace[],
  chosen: ReadonlyMap<string, { signerUserId: string; capacityText: string }>,
): { document_id: string; slot_key: string; signer_user_id: string; capacity_text: string }[] {
  return places.flatMap((place) => {
    const choice = chosen.get(place.slotKey);
    return choice === undefined
      ? []
      : [
          {
            document_id: documentId,
            slot_key: place.slotKey,
            signer_user_id: choice.signerUserId,
            capacity_text: choice.capacityText.trim(),
          },
        ];
  });
}

/**
 * Whether a revision changes anything the live version holds (CCM-R1-SIGN-WEB-C, cut 1006-7's N5):
 * a place's signer or title, or the note, each as the sheet would send it. The server refuses a
 * revision that changes nothing ("Nothing changed in this revision."), so "Change the signer or
 * title" keeps Send off until something differs. The channel and the time zone go back as the live
 * version has them, so they never differ here.
 */
export function revisionChanges(
  live: SigningRequestDetail,
  designations: readonly {
    document_id: string;
    slot_key: string;
    signer_user_id: string;
    capacity_text: string;
  }[],
  note: string | null,
): boolean {
  const slots = new Map(
    live.slots.map((slot) => [`${slot.documentId}|${slot.slotKey}`, slot] as const),
  );
  if (designations.length !== slots.size) return true;
  const placeChanged = designations.some((one) => {
    const slot = slots.get(`${one.document_id}|${one.slot_key}`);
    return (
      slot === undefined ||
      slot.signerUserId !== one.signer_user_id ||
      slot.capacityText !== one.capacity_text
    );
  });
  const liveNote = live.version.message?.trim() ?? '';
  return placeChanged || (liveNote.length > 0 ? liveNote : null) !== note;
}

// ── Request status ─────────────────────────────────────────────────────────────────────────────

/**
 * The request a case's status card shows: its newest open one, else its newest completed one, else
 * its newest that expired or was cancelled. A request a newer one replaced never shows.
 */
export function statusRequestOf(rows: readonly SigningRequestRow[]): SigningRequestRow | undefined {
  const newestFirst = [...rows].sort((a, b) =>
    (b.createdAt ?? '').localeCompare(a.createdAt ?? ''),
  );
  return (
    newestFirst.find((row) => row.state === 'open') ??
    newestFirst.find((row) => row.state === 'completed') ??
    newestFirst.find((row) => row.state === 'expired' || row.state === 'cancelled')
  );
}

/** A person by the roster, or the notices' words for someone it cannot name. */
export function nameOf(personName: PersonName, userId: string | undefined): string {
  return (userId !== undefined ? personName?.(userId) : undefined) ?? SOMEONE_IN_YOUR_ORGANIZATION;
}

/** "Sent Nov 16, 2026 by Maya Solberg · sign by November 30, 2026"; an unnamed sender drops "by". */
export function sentLine(detail: SigningRequestDetail, personName: PersonName): string {
  const zone = detail.version.timeZone;
  const firstSent = detail.request.createdAt ?? detail.version.issuedAt;
  const by = detail.request.issuedByUserId;
  const named = by !== undefined ? personName?.(by) : undefined;
  const sent =
    named !== undefined
      ? `${shortDateIn(firstSent, zone)} by ${named}`
      : shortDateIn(firstSent, zone);
  return `Sent ${sent} · sign by ${longDateIn(detail.request.expiresAt, zone)}`;
}

/** The status card's heading for the request's state. */
export function statusHeading(detail: SigningRequestDetail): string {
  if (detail.request.state === 'expired') return EXPIRED;
  if (detail.request.state === 'cancelled') return CANCELLED;
  return OUT_FOR_SIGNATURE;
}

/** "President, as your organization states it · Signature on page 4". */
export function placeSubLine(slot: SigningSlot): string {
  return `${slot.capacityText}, as your organization states it · ${placeLine(slot.page)}`;
}

/** The latest act of one kind covering a place, if any. */
export function latestActOn(
  detail: SigningRequestDetail,
  slot: SigningSlot,
  kind: SigningAct['kind'],
): SigningAct | undefined {
  return detail.acts
    .filter((act) => act.kind === kind && act.slotIds.includes(slot.slotId))
    .sort((a, b) => (a.occurredAt ?? '').localeCompare(b.occurredAt ?? ''))
    .at(-1);
}

/**
 * A place's state word, in words, never in color: "Waiting", "Declined Nov 17, 2026", "Can’t sign"
 * on an open request; "Not signed" once it expired or was cancelled; and, only where a request has
 * two or more places, S12's "Signed {date}" for a place already signed.
 */
export function placeState(detail: SigningRequestDetail, slot: SigningSlot): string {
  const zone = detail.version.timeZone;
  if (slot.standing === 'signed') return `Signed ${shortDateIn(slot.standingActAt, zone)}`;
  if (detail.request.state !== 'open') return NOT_SIGNED;
  if (slot.cannotSignLostAccess) return CANT_SIGN;
  if (slot.standing === 'declined') return `Declined ${shortDateIn(slot.standingActAt, zone)}`;
  return WAITING;
}

/** Frame 42: a decline, with its reason where one was given. A decline is not final. */
export function declinedLine(
  name: string,
  at: string | undefined,
  reason: string | undefined,
  zone?: string,
): string {
  const on = shortDateIn(at, zone);
  const trimmed = reason?.trim() ?? '';
  return trimmed.length > 0
    ? `${name} declined on ${on}: “${trimmed}” They can still sign it, or you can change the signer or title.`
    : `${name} declined on ${on}. They can still sign it, or you can change the signer or title.`;
}

/**
 * Frame 44: a signer who lost access to Compliance before signing. The Admin Console's page is
 * "Admin Team" (D-CPO-78; CCM-R1-SIGN-ADMIN-TEAM-NAME), so the line names it so.
 */
export function cantSignLine(name: string): string {
  return `${name} no longer has access to Compliance, so they can’t sign. Change the signer, or give them access again in Admin Team.`;
}

/**
 * S6 (approved by Jonathan on this cut's renders, D-CCM-32; D-SIGN-12): a place signed whose signer
 * has since lost access. The slice's line less its loss date, which the backend doesn't serve (J-e
 * item 5).
 */
export function signedThenLostLine(name: string, at: string | undefined, zone?: string): string {
  return `${name} signed on ${shortDateIn(at, zone)}, then lost access to Compliance. The signature stands. If you’d rather not use it, change the signers or cancel the request.`;
}

/** Frame 43a. */
export function expiredLine(detail: SigningRequestDetail): string {
  return `Expired on ${longDateIn(detail.request.expiresAt, detail.version.timeZone)}, before it was signed. Send it again when you’re ready.`;
}

/** Frame 43b; an unnamed person reads as the notices' words. */
export function cancelledLine(detail: SigningRequestDetail, personName: PersonName): string {
  const by = detail.request.cancelledByUserId;
  const named = by !== undefined ? personName?.(by) : undefined;
  return `Cancelled by ${named ?? SOMEONE_IN_YOUR_ORGANIZATION_MID_SENTENCE} on ${shortDateIn(
    detail.request.cancelledAt,
    detail.version.timeZone,
  )}.`;
}

/** "Daniel Okonkwo", "Daniel Okonkwo and Priya Raman", "A, B and C". */
function andList(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * The people the request names, by the roster, each once. The list opens its sentence, so only
 * its first name takes the capital where the roster can't name them (D-CCM-99).
 */
export function signerNames(detail: SigningRequestDetail, personName: PersonName): string {
  const ids = [...new Set(detail.slots.map((slot) => slot.signerUserId))];
  return andList(
    ids.map((id, index) =>
      index === 0
        ? nameOf(personName, id)
        : (personName?.(id) ?? SOMEONE_IN_YOUR_ORGANIZATION_MID_SENTENCE),
    ),
  );
}

/** "Change an answer?" — what cancelling for an answer means for the signers. */
export function changeAnswerBody(detail: SigningRequestDetail, personName: PersonName): string {
  return `This cancels the request out for signature. ${signerNames(detail, personName)} won’t be able to sign this version. You can send the changed form when it’s ready.`;
}

/** "Cancel this request?" */
export function cancelRequestBody(detail: SigningRequestDetail, personName: PersonName): string {
  return `${signerNames(detail, personName)} won’t be able to sign it. You can send the form again.`;
}

// ── The signed form (frame 46) ─────────────────────────────────────────────────────────────────

/** "Signed on November 17, 2026." */
export function signedOnLine(detail: SigningRequestDetail): string {
  return `Signed on ${longDateIn(detail.request.completedAt, detail.version.timeZone)}.`;
}

/** "11/17/2026": the date drawn beside a signature, in the request's zone (the mark's date). */
export function markDate(iso: string | undefined, timeZone?: string): string {
  const day = localDayOf(iso, timeZone);
  if (day === undefined) return '—';
  const [year, month, date] = day.split('-');
  return `${month}/${date}/${year}`;
}

/**
 * The signature block's two lines once signed: "Daniel Okonkwo · President · 11/17/2026" over
 * "Drawn on the form when Daniel Okonkwo signed, Nov 17, 2026". The name is the roster's; the read
 * serves no typed name. One the roster can't name stands alone in the first line and mid-sentence
 * in the second (D-CCM-99).
 */
export function signatureRowLines(
  name: string | undefined,
  slot: SigningSlot,
  timeZone?: string,
): readonly [string, string] {
  return [
    `${name ?? SOMEONE_IN_YOUR_ORGANIZATION} · ${slot.capacityText} · ${markDate(slot.standingActAt, timeZone)}`,
    `Drawn on the form when ${name ?? SOMEONE_IN_YOUR_ORGANIZATION_MID_SENTENCE} signed, ${shortDateIn(slot.standingActAt, timeZone)}`,
  ];
}
